import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { describeError } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import { auth, useSession } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { zh } from "@/i18n/zh";

export function LoginPage() {
  const authed = useSession((s) => s.authed);
  const nav = useNavigate();
  // 只接受站内路径（`//evil.com` 之类会让 history 抛错）
  const raw = (useLocation().state as { from?: string } | null)?.from;
  const from = raw && /^\/(?!\/)/.test(raw) ? raw : "/";
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (authed && !busy) return <Navigate to={from} replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const id = account.trim();
    try {
      const second = !needTotp ? {} : useRecovery ? { recovery_code: totp.trim() } : { totp: totp.trim() };
      const r = await auth.login({ ...(id.includes("@") ? { email: id } : { phone: id }), password, ...second });
      if (r.totp_reset) toast(zh.login.totpReset, "info");
      const s = useSession.getState();
      nav(s.needsTotpSetup || s.needsPasswordChange || r.totp_reset ? "/account" : from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "auth.totp_required") {
        setNeedTotp(true);
        if (needTotp) setError(describeError(err));
      } else if (needTotp && err instanceof ApiError && err.code === "auth.invalid") {
        setError(zh.login.invalidWithTotp);
      } else {
        setError(describeError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardBody>
          <h1 className="mb-1 text-lg font-semibold">{zh.app.name}</h1>
          <p className="mb-5 text-sm text-muted-foreground">{zh.login.title}</p>
          <form className="grid gap-4" onSubmit={submit}>
            <Field label={zh.login.account}>
              <Input
                name="account"
                autoComplete="username"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                required
                autoFocus
              />
            </Field>
            <Field label={zh.login.password}>
              <Input
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
            {needTotp ? (
              <div className="grid gap-1">
                {useRecovery ? (
                  <Field label={zh.login.recoveryCode} hint={zh.login.recoveryHint}>
                    <Input name="recovery-code" autoComplete="off" value={totp} onChange={(e) => setTotp(e.target.value)} required autoFocus />
                  </Field>
                ) : (
                  <Field label={zh.login.totp} hint={zh.login.totpHint}>
                    <Input
                      name="totp"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      value={totp}
                      onChange={(e) => setTotp(e.target.value)}
                      required
                      autoFocus
                    />
                  </Field>
                )}
                <button
                  type="button"
                  className="justify-self-start text-xs text-muted-foreground underline"
                  onClick={() => {
                    setUseRecovery(!useRecovery);
                    setTotp("");
                    setError(null);
                  }}
                >
                  {useRecovery ? zh.login.useTotp : zh.login.useRecovery}
                </button>
              </div>
            ) : null}
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={busy}>
              {busy ? zh.login.submitting : zh.login.submit}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
